import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { startupAPI, fileAPI, pitchEventAPI, investmentRoundAPI } from '../lib/api';
import AlertDialog from '../components/AlertDialog';
import ConfirmDialog from '../components/ConfirmDialog';
import FileUpload from '../components/FileUpload';
import FileShareModal from '../components/FileShareModal';
import FileTraceModal from '../components/FileTraceModal';
import AuditLogTable from '../components/AuditLogTable';
import CreateStartupModal from '../components/CreateStartupModal';
import ChangePasswordModal from '../components/ChangePasswordModal';
import InvestmentTabbedForm from '../components/investments/InvestmentTabbedForm';
import InvestmentTabbedDetails from '../components/investments/InvestmentTabbedDetails';
import {
	buildInvestmentUpdatePayload,
	createEmptyInvestmentFormState,
	investmentToFormState
} from '../components/investments/investmentFormUtils';
import { getApiUrl } from '../config/api.config';
import Logo from '../components/Logo';

const dashboardValidationBadgeClass = (status) => {
	if (status === 'VALIDATED') return 'bg-emerald-100 text-emerald-800 border-emerald-200';
	if (status === 'REJECTED') return 'bg-red-100 text-red-800 border-red-200';
	return 'bg-amber-100 text-amber-800 border-amber-200';
};

const dashboardValidationLabel = (status) => {
	if (!status || status === 'PENDING') return 'Pending review';
	const map = { VALIDATED: 'Validated', REJECTED: 'Rejected' };
	return map[status] || String(status).replace(/_/g, ' ');
};

const StartupDashboard = () => {
	const { user, logout } = useAuth();
	const navigate = useNavigate();
	const { tenantId } = useParams();
	const [searchParams, setSearchParams] = useSearchParams();
	const TAB_IDS = ['overview', 'investors', 'events', 'files', 'investments', 'applications', 'stage-history', 'directors', 'audit'];
	const [activeTab, setActiveTab] = useState(() => {
		const t = searchParams.get('tab');
		return (t && TAB_IDS.includes(t)) ? t : 'overview';
	});
	const [startup, setStartup] = useState(null);
	const [startupProfile, setStartupProfile] = useState(null);
	const [files, setFiles] = useState([]);
	const [investors, setInvestors] = useState([]);
	const [events, setEvents] = useState([]);
	const [directors, setDirectors] = useState([]);
	const [investments, setInvestments] = useState([]);
	const [rounds, setRounds] = useState([]);
	const [myApplications, setMyApplications] = useState([]);
	const [applicationTargets, setApplicationTargets] = useState({ INVESTOR: [], INVESTOR_NETWORK: [] });
	const [applicationForm, setApplicationForm] = useState({
		targetType: 'INVESTOR_NETWORK',
		targetUserId: '',
		pitchDeckFileId: '',
		message: ''
	});
	const [submittingApplication, setSubmittingApplication] = useState(false);
	const [uploadingApplicationDeck, setUploadingApplicationDeck] = useState(false);
	const [stageHistory, setStageHistory] = useState([]);
	const [loading, setLoading] = useState(false);
	const [uploading, setUploading] = useState(false);
	const [showUploadModal, setShowUploadModal] = useState(false);
	const [showEditModal, setShowEditModal] = useState(false);
	const [editModalDirectorsOnly, setEditModalDirectorsOnly] = useState(false);
	const [showPasswordModal, setShowPasswordModal] = useState(false);
	const [showShareModal, setShowShareModal] = useState(false);
	const [showProfileMenu, setShowProfileMenu] = useState(false);
	const [sharingFile, setSharingFile] = useState(null);
	const [selectedFile, setSelectedFile] = useState(null);
	const [uploadProgress, setUploadProgress] = useState(0);
	const [searchTerm, setSearchTerm] = useState('');
	const [dragActive, setDragActive] = useState(false);
	const [alert, setAlert] = useState({ isOpen: false, title: '', message: '', type: 'info' });
	const [confirm, setConfirm] = useState({ isOpen: false, title: '', message: '', onConfirm: null, type: 'warning' });
	const [traceFile, setTraceFile] = useState(null);
	const [showTraceModal, setShowTraceModal] = useState(false);
	const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
	const [showMobileSidebar, setShowMobileSidebar] = useState(false);
	const [filesPage, setFilesPage] = useState(1);
	const [expandedInvestmentId, setExpandedInvestmentId] = useState(null);
	const [showEditInvestmentModal, setShowEditInvestmentModal] = useState(false);
	const [editingInvestmentId, setEditingInvestmentId] = useState(null);
	const [editingInvestmentForm, setEditingInvestmentForm] = useState(createEmptyInvestmentFormState);
	const [savingInvestment, setSavingInvestment] = useState(false);

	useEffect(() => {
		if (user) {
			loadStartupData();
		}
	}, [user]);

	// Update URL when tab changes
	useEffect(() => {
		setSearchParams({ tab: activeTab });
	}, [activeTab, setSearchParams]);

	// Sync activeTab when URL query param changes (e.g., browser back/forward)
	useEffect(() => {
		const tabFromUrl = searchParams.get('tab');
		if (tabFromUrl && TAB_IDS.includes(tabFromUrl)) {
			setActiveTab(tabFromUrl);
		}
	}, [searchParams]);

	// For Investors tab: show investor networks first, then individual investors
	const investorGroups = useMemo(() => {
		const networkMap = new Map();
		const individualMap = new Map();

		const addNetwork = (item, key) => {
			if (!key || networkMap.has(key)) return;
			networkMap.set(key, { ...item, entityType: 'NETWORK' });
		};
		const addIndividual = (item, key) => {
			if (!key || individualMap.has(key)) return;
			individualMap.set(key, { ...item, entityType: 'INDIVIDUAL' });
		};

		// 1) Explicit network commitments from rounds
		(rounds || []).forEach((round) => {
			(round.commitments || []).forEach((c) => {
				const networkLabel = c.networkName || c.investorName || c.networkEmail;
				if (!networkLabel) return;
				const key = `network-${c.networkId || c.investorId || c.networkEmail || networkLabel}`;
				addNetwork({
					id: c.networkId || c.investorId || key,
					name: networkLabel,
					organizationName: networkLabel,
					email: c.networkEmail || c.investorEmail || null
				}, key);
			});
		});

		// 2) Investors endpoint (may include both investors and network users)
		(investors || []).forEach((inv) => {
			const label = (inv.name || inv.organizationName || 'Investor').trim();
			const key = `user-${inv.id || inv.email || label}`;
			const isNetwork = inv.role === 'INVESTOR_NETWORK';
			const data = {
				id: inv.id || key,
				name: label,
				organizationName: inv.organizationName || inv.name,
				email: inv.email || null
			};
			if (isNetwork) addNetwork(data, key);
			else addIndividual(data, key);
		});

		// 3) Direct investments fallback
		(investments || []).forEach((inv) => {
			const label = (inv.investorName || 'Investor').trim();
			// Use the same key scheme as the `/investments/.../investors` endpoint
			// so the same investor is not duplicated across both passes.
			const key = `user-${inv.investorId || inv.investorEmail || label}`;
			const data = {
				id: inv.investorId || key,
				name: label,
				organizationName: label,
				email: inv.investorEmail || null
			};
			if (inv.investorRole === 'INVESTOR_NETWORK') addNetwork(data, key);
			else if (inv.investorRole === 'INVESTOR') addIndividual(data, key);
			else {
				const looksLikeNetwork = label.toLowerCase().includes('network');
				if (looksLikeNetwork) addNetwork(data, key);
				else addIndividual(data, key);
			}
		});

		const networkInvestors = Array.from(networkMap.values());
		const individualInvestors = Array.from(individualMap.values()).filter((inv) => {
			const byEmail = inv.email && networkInvestors.some((n) => n.email && n.email === inv.email);
			const byName = networkInvestors.some((n) => n.name === inv.name);
			return !byEmail && !byName;
		});

		return {
			networkInvestors,
			individualInvestors,
			all: [...networkInvestors, ...individualInvestors]
		};
	}, [investors, investments, rounds]);

	/** Cap table rows: syndicate (INVESTOR_NETWORK user) vs individual INVESTOR accounts */
	const capInvestmentGroups = useMemo(() => {
		const list = Array.isArray(investments) ? investments : [];
		const network = [];
		const individual = [];
		for (const inv of list) {
			if (inv.investorRole === 'INVESTOR_NETWORK') network.push(inv);
			else individual.push(inv);
		}
		return { network, individual };
	}, [investments]);

	const loadStartupData = async () => {
		setLoading(true);
		try {
			// Load user/startup details - use startup endpoint instead of admin
			const userResponse = await fetch(getApiUrl(`users/me`), {
				headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
			});

			if (!userResponse.ok) {
				throw new Error('Failed to load user data');
			}

			const userData = await userResponse.json();
			setStartup(userData);

			// Load startup profile (own profile via users/me/startup-profile)
			try {
				const profileData = await startupAPI.getMyStartupProfile();
				setStartupProfile(profileData);
				// If no startup record yet, redirect to complete-profile
				if (!profileData.startupId) {
					navigate(`/${tenantId || 'default'}/startup/complete-profile`);
					return;
				}
			} catch (err) {
				console.log('Startup profile not found or incomplete');
				navigate(`/${tenantId || 'default'}/startup/complete-profile`);
				return;
			}

			// Load files
			const filesData = await fileAPI.getFilesByModule('STARTUP', user.id);
			setFiles(filesData.map(file => ({ ...file, tags: file.tags || [] })));

			// Load investors (who have invested in this startup)
			try {
				const investorsResponse = await fetch(getApiUrl(`investments/startup/${user.id}/investors`), {
					headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
				});
				if (investorsResponse.ok) {
					const investorsData = await investorsResponse.json();
					setInvestors(Array.isArray(investorsData) ? investorsData : []);
				} else {
					setInvestors([]);
				}
			} catch (err) {
				console.error('Failed to load investors:', err);
				setInvestors([]);
			}

			// Load events
			try {
				const eventsData = await pitchEventAPI.getByStartup(user.id);
				setEvents(eventsData || []);
			} catch (err) {
				console.error('Failed to load events:', err);
			}

			// Load directors (own profile)
			try {
				const directorsData = await startupAPI.getMyStartupDirectors();
				setDirectors(directorsData || []);
			} catch (err) {
				console.error('Failed to load directors:', err);
				setDirectors([]);
			}

			// Load investments
			try {
				const invResponse = await fetch(getApiUrl(`investments/startup/${user.id}`), {
					headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
				});
				if (invResponse.ok) {
					const invData = await invResponse.json();
					setInvestments(Array.isArray(invData) ? invData : []);
				} else {
					setInvestments([]);
				}
			} catch (err) {
				console.error('Failed to load investments:', err);
				setInvestments([]);
			}

			// Load investment rounds
			try {
				const roundsData = await investmentRoundAPI.getRoundsByStartup(user.id);
				setRounds(Array.isArray(roundsData) ? roundsData : []);
			} catch (err) {
				console.error('Failed to load rounds:', err);
				setRounds([]);
			}

			// Stage history (derive from current stage for now)
			const stage = userData.jobTitle || userData.stage;
			if (stage) {
				setStageHistory([{ stage, date: new Date().toISOString().split('T')[0], amount: null }]);
			} else {
				setStageHistory([]);
			}

			try {
				const [applicationsData, investorTargets, networkTargets] = await Promise.all([
					startupAPI.getMyInvestmentApplications(),
					startupAPI.getApplicationTargets('INVESTOR'),
					startupAPI.getApplicationTargets('INVESTOR_NETWORK')
				]);
				setMyApplications(Array.isArray(applicationsData) ? applicationsData : []);
				setApplicationTargets({
					INVESTOR: Array.isArray(investorTargets) ? investorTargets : [],
					INVESTOR_NETWORK: Array.isArray(networkTargets) ? networkTargets : []
				});
			} catch (err) {
				console.error('Failed to load startup applications data:', err);
				setMyApplications([]);
			}
		} catch (err) {
			console.error('Failed to load startup data:', err);
		} finally {
			setLoading(false);
		}
	};

	const handleFileUpload = async (file) => {
		if (!file) return;

		setUploading(true);
		setUploadProgress(0);
		try {
			await fileAPI.uploadFileWithProgress(file, (progress) => {
				setUploadProgress(progress);
			});
			await loadStartupData();
			setShowUploadModal(false);
			setSelectedFile(null);
		} catch (err) {
			setAlert({ isOpen: true, title: 'Error', message: err.response?.data?.error || 'Failed to upload file', type: 'error' });
		} finally {
			setUploading(false);
			setUploadProgress(0);
		}
	};

	const handleShare = (file) => {
		setSharingFile(file);
		setShowShareModal(true);
	};

	const handleDrag = (e) => {
		e.preventDefault();
		e.stopPropagation();
		if (e.type === 'dragenter' || e.type === 'dragover') {
			setDragActive(true);
		} else if (e.type === 'dragleave') {
			setDragActive(false);
		}
	};

	const handleDrop = (e) => {
		e.preventDefault();
		e.stopPropagation();
		setDragActive(false);

		if (e.dataTransfer.files && e.dataTransfer.files[0]) {
			setSelectedFile(e.dataTransfer.files[0]);
		}
	};

	const previewFile = async (file) => {
		try {
			const response = await fileAPI.getFilePreview(file.id);
			window.open(response.previewUrl, '_blank');
		} catch (err) {
			alert('Failed to preview file');
		}
	};

	const downloadFile = async (file) => {
		try {
			const response = await fileAPI.getFileDownload(file.id);
			// Create blob from response data
			const blob = new Blob([response.data], { type: response.headers?.['content-type'] || 'application/octet-stream' });
			// Create object URL
			const url = window.URL.createObjectURL(blob);
			// Create download link
			const link = document.createElement('a');
			link.href = url;
			link.download = file.originalName || file.name || 'download';
			document.body.appendChild(link);
			link.click();
			// Cleanup
			document.body.removeChild(link);
			window.URL.revokeObjectURL(url);
		} catch (err) {
			console.error('Failed to download file:', err);
			alert('Failed to download file');
		}
	};

	const deleteFile = (fileId) => {
		setConfirm({
			isOpen: true,
			title: 'Delete File',
			message: 'Are you sure you want to delete this file?',
			type: 'danger',
			onConfirm: async () => {
				await performDeleteFile(fileId);
			}
		});
	};

	const performDeleteFile = async (fileId) => {
		try {
			await fileAPI.deleteFile(fileId);
			await loadStartupData();
			setAlert({ isOpen: true, title: 'Success', message: 'File deleted successfully', type: 'success' });
		} catch (err) {
			setAlert({ isOpen: true, title: 'Error', message: 'Failed to delete file', type: 'error' });
		}
	};

	const handleLogout = () => {
		logout();
		navigate('/login');
	};

	const formatDate = (dateString) => {
		if (!dateString) return 'N/A';
		return new Date(dateString).toLocaleDateString();
	};

	const formatDateTime = (dateString) => {
		if (!dateString) return 'N/A';
		return new Date(dateString).toLocaleString();
	};

	const formatMoney = (amount, currency = 'INR') => {
		if (amount == null || amount === '') return '—';
		const n = typeof amount === 'number' ? amount : Number(String(amount));
		if (Number.isNaN(n)) return '—';
		return new Intl.NumberFormat('en-IN', { style: 'currency', currency: currency || 'INR', maximumFractionDigits: 2 }).format(n);
	};

	const roundStatusBadgeClass = (status) => {
		const s = (status || '').toUpperCase();
		if (s === 'OPEN') return 'bg-blue-100 text-blue-800';
		if (s === 'CLOSED') return 'bg-gray-100 text-gray-800';
		return 'bg-slate-100 text-slate-700';
	};

	const canStartupEditInvestment = (investment) => {
		if (!investment) return false;
		return investment.investorRole !== 'INVESTOR_NETWORK';
	};

	const openEditInvestment = (investment) => {
		if (!canStartupEditInvestment(investment)) {
			setAlert({
				isOpen: true,
				title: 'Edit restricted',
				message: 'Investments added by investor network cannot be edited by startup.',
				type: 'warning'
			});
			return;
		}
		setEditingInvestmentId(investment.id);
		setEditingInvestmentForm(investmentToFormState(investment));
		setShowEditInvestmentModal(true);
	};

	const closeEditInvestmentModal = () => {
		setShowEditInvestmentModal(false);
		setEditingInvestmentId(null);
		setEditingInvestmentForm(createEmptyInvestmentFormState());
		setSavingInvestment(false);
	};

	const handleUpdateInvestment = async () => {
		if (!editingInvestmentId) return;
		if (!editingInvestmentForm.investmentAmount || Number(editingInvestmentForm.investmentAmount) <= 0) {
			setAlert({ isOpen: true, title: 'Validation', message: 'Enter a valid investment amount.', type: 'error' });
			return;
		}

		setSavingInvestment(true);
		try {
			const response = await fetch(getApiUrl(`investments/${editingInvestmentId}`), {
				method: 'PUT',
				headers: {
					'Content-Type': 'application/json',
					'Authorization': `Bearer ${localStorage.getItem('token')}`
				},
				body: JSON.stringify(buildInvestmentUpdatePayload(editingInvestmentForm))
			});

			if (!response.ok) {
				let message = 'Failed to update investment';
				try {
					const err = await response.json();
					message = err.message || err.error || message;
				} catch {
					// ignore JSON parse failures
				}
				setAlert({ isOpen: true, title: 'Error', message, type: 'error' });
				return;
			}

			closeEditInvestmentModal();
			await loadStartupData();
			setAlert({ isOpen: true, title: 'Success', message: 'Investment updated successfully.', type: 'success' });
		} catch (error) {
			console.error('Failed to update investment:', error);
			setAlert({ isOpen: true, title: 'Error', message: 'Failed to update investment.', type: 'error' });
		} finally {
			setSavingInvestment(false);
		}
	};

	const filteredFiles = files.filter(file =>
		file.originalName.toLowerCase().includes(searchTerm.toLowerCase())
	);
	const FILES_PER_PAGE = 10;
	const totalFilePages = Math.max(1, Math.ceil(filteredFiles.length / FILES_PER_PAGE));
	const paginatedFiles = filteredFiles.slice((filesPage - 1) * FILES_PER_PAGE, filesPage * FILES_PER_PAGE);

	useEffect(() => {
		setFilesPage(1);
	}, [searchTerm, files.length]);

	useEffect(() => {
		if (filesPage > totalFilePages) {
			setFilesPage(totalFilePages);
		}
	}, [filesPage, totalFilePages]);

	const sidebarItems = [
		{ id: 'overview', label: 'Overview', icon: 'M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6z' },
		{ id: 'investors', label: 'Investors', icon: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z' },
		{ id: 'events', label: 'Events', icon: 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z' },
		{ id: 'files', label: 'Files', icon: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z' },
		{ id: 'investments', label: 'Investments', icon: 'M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z' },
		{ id: 'applications', label: 'Applications', icon: 'M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2V9l-4-4H9zM9 3v6h6' },
		{ id: 'stage-history', label: 'Stage History', icon: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z' },
		{ id: 'directors', label: 'Directors', icon: 'M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z' }
	];

	return (
		<div className="min-h-screen bg-[#f8fafc] flex">
			{/* Left Sidebar - collapsible */}
			<aside className={`${sidebarCollapsed ? 'lg:w-20' : 'lg:w-64'} w-64 bg-white border-r border-gray-200 flex flex-col fixed h-full shadow-sm z-40 transition-all duration-300 ease-in-out ${showMobileSidebar ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0`}>
				<div className={`border-b border-gray-100 flex items-center ${sidebarCollapsed ? 'p-3 justify-center' : 'p-6'}`}>
					<div className={`flex flex-col space-y-1 overflow-hidden ${sidebarCollapsed ? 'items-center' : ''}`}>
						<Logo width={40} height={40} showText={!sidebarCollapsed} className="[&_span]:!text-xl" />
						{!sidebarCollapsed && <p className="text-xs text-gray-500 pl-[52px]">Startup Portal</p>}
					</div>
					<button
						onClick={() => setShowMobileSidebar(false)}
						className="lg:hidden ml-auto p-2 rounded-md text-gray-500 hover:bg-gray-100"
					>
						<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
							<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
						</svg>
					</button>
				</div>
				<div className={`mt-4 overflow-hidden ${sidebarCollapsed ? 'px-3' : 'p-4 mx-4'}`}>
					<div className={`flex items-center ${sidebarCollapsed ? 'justify-center' : 'space-x-3'}`}>
						<div className="w-10 h-10 flex-shrink-0 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-full flex items-center justify-center shadow-md">
							<span className="text-white font-semibold">{user?.name?.charAt(0)?.toUpperCase()}</span>
						</div>
						{!sidebarCollapsed && (
							<div className="flex-1 min-w-0">
								<p className="text-sm font-semibold text-gray-900 truncate">{user?.name}</p>
								<p className="text-xs text-gray-500 truncate">{startup?.organizationName || user?.email}</p>
							</div>
						)}
					</div>
				</div>
				<nav className="flex-1 p-4 space-y-1 overflow-y-auto">
					{sidebarItems.map((item) => (
						<button
							key={item.id}
							onClick={() => {
								setActiveTab(item.id);
								setSearchParams({ tab: item.id });
								setShowMobileSidebar(false);
							}}
							title={sidebarCollapsed ? item.label : undefined}
							className={`w-full flex items-center ${sidebarCollapsed ? 'justify-center px-0 py-3' : 'space-x-3 px-4 py-3'} rounded-xl text-left transition-all duration-200 ${activeTab === item.id
								? 'bg-gradient-to-r from-emerald-600 to-teal-600 text-white shadow-lg shadow-emerald-600/30'
								: 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
								}`}
						>
							<svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={item.icon} />
							</svg>
							{!sidebarCollapsed && <span className="font-medium">{item.label}</span>}
						</button>
					))}
				</nav>
				<div className={`border-t border-gray-100 space-y-2 ${sidebarCollapsed ? 'p-2' : 'p-4'}`}>
					<button
						onClick={() => setSidebarCollapsed((c) => !c)}
						title={sidebarCollapsed ? 'Expand menu' : 'Collapse menu'}
						className={`hidden lg:flex w-full items-center ${sidebarCollapsed ? 'justify-center px-0 py-2.5 mt-2' : 'space-x-3 px-4 py-2.5 mt-2'} rounded-lg text-gray-500 hover:bg-gray-100 border-t border-gray-100 pt-3 transition-colors`}
					>
						<svg className={`w-5 h-5 flex-shrink-0 transition-transform duration-300 ${sidebarCollapsed ? '' : 'rotate-180'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
							<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
						</svg>
						{!sidebarCollapsed && <span className="text-sm font-medium">Collapse menu</span>}
					</button>
				</div>
			</aside>
			{showMobileSidebar && (
				<div className="fixed inset-0 bg-black/30 z-30 lg:hidden" onClick={() => setShowMobileSidebar(false)} />
			)}

			{/* Main content */}
			<main className={`flex-1 min-h-screen transition-[margin] duration-300 ease-in-out ml-0 ${sidebarCollapsed ? 'lg:ml-20' : 'lg:ml-64'}`}>
				<header className="sticky top-0 z-20 bg-white/80 backdrop-blur-md border-b border-gray-200/80 shadow-sm">
					<div className="px-6 lg:px-8 py-4">
						<div className="flex justify-between items-center">
							<div>
								<button
									onClick={() => setShowMobileSidebar(true)}
									className="lg:hidden mb-2 inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-gray-200 bg-white text-gray-700"
								>
									<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
										<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
									</svg>
									<span className="text-sm font-medium">Menu</span>
								</button>
								<h1 className="text-xl font-bold text-gray-900 tracking-tight">
									{activeTab === 'overview' && 'Overview'}
									{activeTab === 'investors' && 'Investors'}
									{activeTab === 'events' && 'Events'}
									{activeTab === 'files' && 'Files'}
									{activeTab === 'investments' && 'Investments'}
									{activeTab === 'applications' && 'Investment Applications'}
									{activeTab === 'stage-history' && 'Stage History'}
									{activeTab === 'directors' && 'Directors'}
									{activeTab === 'audit' && 'Audit'}
								</h1>
								<div className="text-sm text-gray-500 mt-0.5 flex flex-wrap items-center gap-2">
									<span>{startup?.organizationName || startup?.name || 'Dashboard'}</span>
									{startupProfile?.startupId != null && (
										<span
											className={`text-xs font-semibold uppercase tracking-wide px-2 py-0.5 rounded-md border ${dashboardValidationBadgeClass(startupProfile?.validationStatus || 'PENDING')}`}
											title="Platform validation status"
										>
											{dashboardValidationLabel(startupProfile?.validationStatus || 'PENDING')}
										</span>
									)}
								</div>
							</div>
							<div className="relative">
								<button
									onClick={() => setShowProfileMenu((v) => !v)}
									className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 transition-colors"
								>
									<span className="text-sm font-medium">More</span>
									<svg className={`w-4 h-4 transition-transform ${showProfileMenu ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
										<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
									</svg>
								</button>
								{showProfileMenu && (
									<div className="absolute right-0 mt-2 w-64 bg-white border border-gray-200 rounded-xl shadow-lg z-40 py-2">
										<button onClick={() => { setActiveTab('audit'); setSearchParams({ tab: 'audit' }); setShowProfileMenu(false); }} className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50">Audit</button>
										<div className="my-1 border-t border-gray-100" />
										<button onClick={() => { navigate(`/${user?.tenantIdentifier || 'default'}/my-subscription`); setShowProfileMenu(false); }} className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50">Subscription</button>
										<button onClick={() => { navigate(`/${user?.tenantIdentifier || 'default'}/billing`); setShowProfileMenu(false); }} className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50">Billing</button>
										<button onClick={() => { setShowPasswordModal(true); setShowProfileMenu(false); }} className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50">Change Password</button>
										<button onClick={() => { setShowProfileMenu(false); handleLogout(); }} className="w-full text-left px-4 py-2.5 text-sm text-red-600 hover:bg-red-50">Logout</button>
									</div>
								)}
							</div>
						</div>
					</div>
				</header>

			<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

				{loading ? (
					<div className="text-center py-16">
						<div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
							<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
						</div>
						<p className="text-lg font-medium text-gray-900">Loading...</p>
					</div>
				) : (
					<>
						{/* Overview Tab - aligned with admin startup detail */}
						{activeTab === 'overview' && (
							<div className="space-y-4">
								<div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
									{/* Quick Stats */}
									<div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
										<h3 className="text-sm font-medium text-gray-500 mb-4">Quick Stats</h3>
										<div className="space-y-4">
											<div>
												<p className="text-2xl font-bold text-gray-900">{files.length || 0}</p>
												<p className="text-sm text-gray-500">Documents</p>
											</div>
											<div>
												<p className="text-2xl font-bold text-gray-900">{directors.length || 0}</p>
												<p className="text-sm text-gray-500">Directors</p>
											</div>
											<div>
												<p className="text-2xl font-bold text-gray-900">{stageHistory.length || 0}</p>
												<p className="text-sm text-gray-500">Funding stages</p>
											</div>
										</div>
									</div>

									{/* Company Info */}
									<div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
										<div className="flex justify-between items-start mb-4">
											<h3 className="text-lg font-semibold text-gray-900">Company Information</h3>
											<button
												onClick={() => { setEditModalDirectorsOnly(false); setShowEditModal(true); }}
												className="px-4 py-2 text-sm font-medium text-emerald-600 bg-emerald-50 rounded-lg hover:bg-emerald-100 transition"
											>
												Edit Profile
											</button>
										</div>
										<dl className="space-y-3">
											<div>
												<dt className="text-sm font-medium text-gray-500">Company Name</dt>
												<dd className="mt-1 text-sm text-gray-900">{startup?.organizationName || startup?.name || 'N/A'}</dd>
											</div>
											<div>
												<dt className="text-sm font-medium text-gray-500">Industry</dt>
												<dd className="mt-1 text-sm text-gray-900">{startup?.department || 'N/A'}</dd>
											</div>
											<div>
												<dt className="text-sm font-medium text-gray-500">Current Stage</dt>
												<dd className="mt-1">
													<span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-emerald-100 text-emerald-800">
														{startup?.jobTitle || 'Not specified'}
													</span>
												</dd>
											</div>
											{(startupProfile?.website) && (
												<div>
													<dt className="text-sm font-medium text-gray-500">Website</dt>
													<dd className="mt-1 text-sm text-gray-900">
														<a href={(startupProfile.website || '').startsWith('http') ? startupProfile.website : `https://${startupProfile.website}`}
															target="_blank" rel="noopener noreferrer" className="text-emerald-600 hover:text-emerald-800">
															{startupProfile.website}
														</a>
													</dd>
												</div>
											)}
											{startupProfile?.startupId != null && (
												<div>
													<dt className="text-sm font-medium text-gray-500">Platform validation</dt>
													<dd className="mt-1">
														<span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-md border ${dashboardValidationBadgeClass(startupProfile?.validationStatus || 'PENDING')}`}>
															{dashboardValidationLabel(startupProfile?.validationStatus || 'PENDING')}
														</span>
													</dd>
												</div>
											)}
											{startupProfile?.validationNotes && (
												<div>
													<dt className="text-sm font-medium text-gray-500">Validation notes</dt>
													<dd className="mt-1 text-sm text-gray-700 whitespace-pre-wrap">{startupProfile.validationNotes}</dd>
												</div>
											)}
										</dl>
									</div>

									{/* Contact Info */}
									<div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
										<h3 className="text-lg font-semibold text-gray-900 mb-4">Contact Information</h3>
										<dl className="space-y-3">
											<div>
												<dt className="text-sm font-medium text-gray-500">Primary Contact</dt>
												<dd className="mt-1 text-sm text-gray-900">{startup?.name || 'N/A'}</dd>
											</div>
											<div>
												<dt className="text-sm font-medium text-gray-500">Email</dt>
												<dd className="mt-1 text-sm text-gray-900">{startup?.email || 'N/A'}</dd>
											</div>
											<div>
												<dt className="text-sm font-medium text-gray-500">Phone</dt>
												<dd className="mt-1 text-sm text-gray-900">{startup?.phoneNumber || 'N/A'}</dd>
											</div>
										</dl>
									</div>

									{/* Company Location */}
									{(startupProfile?.officialAddress || startupProfile?.branchAddress || startupProfile?.city || startupProfile?.state || startupProfile?.country) && (
										<div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 lg:col-span-2 xl:col-span-1">
											<h3 className="text-lg font-semibold text-gray-900 mb-4">Company Location</h3>
											<dl className="space-y-3">
												{startupProfile.officialAddress && (
													<div>
														<dt className="text-sm font-medium text-gray-500">Official Address</dt>
														<dd className="mt-1 text-sm text-gray-900 whitespace-pre-line">{startupProfile.officialAddress}</dd>
													</div>
												)}
												{startupProfile.branchAddress && (
													<div>
														<dt className="text-sm font-medium text-gray-500">Branch Address</dt>
														<dd className="mt-1 text-sm text-gray-900 whitespace-pre-line">{startupProfile.branchAddress}</dd>
													</div>
												)}
												{(startupProfile.city || startupProfile.state || startupProfile.country) && (
													<div>
														<dt className="text-sm font-medium text-gray-500">Location</dt>
														<dd className="mt-1 text-sm text-gray-900">
															{[startupProfile.city, startupProfile.state, startupProfile.country].filter(Boolean).join(', ') || 'N/A'}
														</dd>
													</div>
												)}
											</dl>
										</div>
									)}
								</div>
							</div>
						)}

						{/* Investors Tab */}
						{activeTab === 'investors' && (
							<div className="overflow-hidden rounded-2xl border border-gray-200/80 bg-white shadow-lg shadow-gray-200/50">
								{/* Section header */}
								<div className="relative px-6 py-5 overflow-hidden bg-gradient-to-br from-emerald-500/10 via-teal-500/5 to-transparent border-b border-gray-100">
									<div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-bl from-emerald-400/10 to-transparent rounded-full -translate-y-1/2 translate-x-1/2" />
									<div className="relative flex flex-wrap items-end justify-between gap-4">
										<div className="flex items-center gap-3">
											<div className="flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 shadow-md shadow-emerald-500/25">
												<svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
													<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
												</svg>
											</div>
											<div>
												<p className="text-sm text-gray-600">People and organizations who have invested in you</p>
											</div>
										</div>
										{investorGroups.all.length > 0 && (
											<div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/80 border border-gray-200/80 shadow-sm">
												<span className="text-xl font-bold text-emerald-600">{investorGroups.all.length}</span>
												<span className="text-sm font-medium text-gray-600">{investorGroups.all.length === 1 ? 'investor' : 'investors'}</span>
											</div>
										)}
									</div>
								</div>
								<div className="p-6">
									{investorGroups.all.length === 0 ? (
										<div className="text-center py-14 px-5 rounded-xl bg-gray-50/80 border-2 border-dashed border-gray-200">
											<div className="inline-flex items-center justify-center w-16 h-16 rounded-xl bg-gradient-to-br from-emerald-100 to-teal-100 text-emerald-500 mb-4">
												<svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
													<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
												</svg>
											</div>
											<h3 className="text-lg font-semibold text-gray-900">No investors yet</h3>
											<p className="mt-2 text-gray-600 max-w-sm mx-auto">Investors will appear here once they complete an investment.</p>
										</div>
									) : (
										<div className="space-y-6">
											{investorGroups.networkInvestors.length > 0 && (
												<div>
													<div className="flex items-center justify-between mb-3">
														<h3 className="text-sm font-semibold tracking-wide text-gray-700 uppercase">Investor Networks</h3>
														<span className="text-xs text-gray-500">{investorGroups.networkInvestors.length}</span>
													</div>
													<div className="grid gap-4 sm:grid-cols-1 md:grid-cols-2 xl:grid-cols-3">
														{investorGroups.networkInvestors.map((investor) => (
															<div
																key={investor.id}
																className="group flex items-center gap-3 p-4 rounded-xl border border-emerald-200 bg-emerald-50/50 hover:bg-white hover:border-emerald-300 hover:shadow-md hover:shadow-emerald-500/10 transition-all duration-200"
															>
																<div className="flex-shrink-0 h-12 w-12 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-md shadow-emerald-500/25 ring-2 ring-white">
																	<span className="text-white text-lg font-bold">
																		{(investor.name || investor.organizationName)?.charAt(0)?.toUpperCase()}
																	</span>
																</div>
																<div className="min-w-0 flex-1">
																	<p className="text-sm font-semibold text-gray-900 truncate">{investor.name || investor.organizationName}</p>
																	<p className="text-sm text-gray-500 truncate mt-0.5">{investor.email || '—'}</p>
																</div>
																<span className="flex-shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-semibold rounded-md bg-teal-100 text-teal-700 border border-teal-200/60">
																	Network
																</span>
															</div>
														))}
													</div>
												</div>
											)}

											{investorGroups.individualInvestors.length > 0 && (
												<div>
													<div className="flex items-center justify-between mb-3">
														<h3 className="text-sm font-semibold tracking-wide text-gray-700 uppercase">Individual Investors</h3>
														<span className="text-xs text-gray-500">{investorGroups.individualInvestors.length}</span>
													</div>
													<div className="grid gap-4 sm:grid-cols-1 md:grid-cols-2 xl:grid-cols-3">
														{investorGroups.individualInvestors.map((investor) => (
															<div
																key={investor.id}
																className="group flex items-center gap-3 p-4 rounded-xl border border-gray-200 bg-gray-50/50 hover:bg-white hover:border-emerald-200/80 hover:shadow-md hover:shadow-emerald-500/10 transition-all duration-200"
															>
																<div className="flex-shrink-0 h-12 w-12 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-md shadow-emerald-500/25 ring-2 ring-white">
																	<span className="text-white text-lg font-bold">
																		{(investor.name || investor.organizationName)?.charAt(0)?.toUpperCase()}
																	</span>
																</div>
																<div className="min-w-0 flex-1">
																	<p className="text-sm font-semibold text-gray-900 truncate">{investor.name || investor.organizationName}</p>
																	<p className="text-sm text-gray-500 truncate mt-0.5">{investor.email || '—'}</p>
																</div>
																<span className="flex-shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-semibold rounded-md bg-emerald-100 text-emerald-700 border border-emerald-200/60">
																	Investor
																</span>
															</div>
														))}
													</div>
												</div>
											)}
										</div>
									)}
								</div>
							</div>
						)}

						{/* Events Tab */}
						{activeTab === 'events' && (
							<div className="bg-white rounded-xl shadow-sm border border-gray-200">
								<div className="px-6 py-4 border-b border-gray-200">
									<p className="text-sm text-gray-500">Pitch events you are participating in</p>
								</div>
								<div className="p-6">
									{events.length === 0 ? (
										<div className="text-center py-12">
											<svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
												<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
											</svg>
											<h3 className="mt-2 text-sm font-medium text-gray-900">No events yet</h3>
											<p className="mt-1 text-sm text-gray-500">Events you participate in will appear here.</p>
										</div>
									) : (
										<div className="space-y-3">
											{events.map((event) => (
												<div key={event.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition">
													<div className="flex items-center space-x-3">
														<div className="h-10 w-10 rounded-full bg-gradient-to-r from-purple-500 to-pink-600 flex items-center justify-center">
															<svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
																<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
															</svg>
														</div>
														<div>
															<p className="text-sm font-medium text-gray-900">{event.eventName}</p>
															<p className="text-xs text-gray-500">
																{formatDateTime(event.eventDate)} • {event.location || 'Location TBD'}
															</p>
														</div>
													</div>
													<div className="text-right">
														<span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${event.status === 'UPCOMING' ? 'bg-blue-100 text-blue-800' :
															event.status === 'ONGOING' ? 'bg-green-100 text-green-800' :
																event.status === 'COMPLETED' ? 'bg-gray-100 text-gray-800' :
																	'bg-red-100 text-red-800'
															}`}>
															{event.status}
														</span>
													</div>
												</div>
											))}
										</div>
									)}
								</div>
							</div>
						)}

						{/* Investments Tab — funding tranches + full investment records */}
						{activeTab === 'investments' && (
							<div className="space-y-8">
								<section className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
									<div className="px-6 py-4 border-b border-gray-200">
										<h2 className="text-lg font-semibold text-gray-900">Funding tranches</h2>
										<p className="text-sm text-gray-500 mt-0.5">
											Tranche targets and valuations. <span className="text-gray-700 font-medium">Network commitments</span> are at syndicate level;
											{' '}<span className="text-gray-700 font-medium">participations</span> (when listed) are individual LPs inside a commitment.
										</p>
									</div>
									<div className="p-6">
										{(!rounds || rounds.length === 0) ? (
											<div className="text-center py-12 px-4 text-gray-500 rounded-xl border-2 border-dashed border-gray-200 bg-gray-50/50">
												<p className="font-medium text-gray-700">No investment tranches yet</p>
												<p className="text-sm mt-2 max-w-md mx-auto">When your team or investor network opens a tranche, it will appear here with commitments and syndication participation.</p>
											</div>
										) : (
											<div className="space-y-6">
												{rounds.map((round) => {
													const targetNum = round.targetAmount != null ? Number(round.targetAmount) : 0;
													const progress = Math.min(Number(round.progressPercentage) || 0, 100);
													return (
														<div key={round.id} className="rounded-xl border border-gray-200 bg-gradient-to-b from-white to-slate-50/80 overflow-hidden shadow-sm">
															<div className="p-5 border-b border-gray-100 flex flex-wrap items-start justify-between gap-3 bg-white/90">
																<div>
																	<h3 className="text-base font-semibold text-gray-900">{round.roundName || 'Tranche'}</h3>
																	<p className="text-xs text-gray-500 mt-1">
																		{round.openDate ? `Opened ${formatDate(round.openDate)}` : 'Open date —'}
																		{round.closeDate ? ` · Close ${formatDate(round.closeDate)}` : ''}
																	</p>
																</div>
																<span className={`inline-flex px-2.5 py-1 text-xs font-semibold rounded-full ${roundStatusBadgeClass(round.status)}`}>
																	{round.status || '—'}
																</span>
															</div>
															<div className="p-5 space-y-4">
																<div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
																	<div>
																		<p className="text-gray-500">Target</p>
																		<p className="font-semibold text-gray-900">{formatMoney(round.targetAmount)}</p>
																	</div>
																	<div>
																		<p className="text-gray-500">Raised</p>
																		<p className="font-semibold text-gray-900">{formatMoney(round.raisedAmount)}</p>
																	</div>
																	<div>
																		<p className="text-gray-500">Pre-money</p>
																		<p className="font-semibold text-gray-900">{formatMoney(round.preMoneyValuation)}</p>
																	</div>
																	<div>
																		<p className="text-gray-500">Post-money</p>
																		<p className="font-semibold text-gray-900">{formatMoney(round.postMoneyValuation)}</p>
																	</div>
																	<div>
																		<p className="text-gray-500">Share price</p>
																		<p className="font-semibold text-gray-900">{formatMoney(round.sharePrice)}</p>
																	</div>
																	<div>
																		<p className="text-gray-500">Total shares</p>
																		<p className="font-semibold text-gray-900">{round.totalShares != null ? Number(round.totalShares).toLocaleString() : '—'}</p>
																	</div>
																	<div>
																		<p className="text-gray-500">Progress</p>
																		<p className="font-semibold text-gray-900">{progress.toFixed(0)}%</p>
																	</div>
																</div>
																{targetNum > 0 && (
																	<div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
																		<div className="bg-emerald-600 h-2 rounded-full transition-all" style={{ width: `${progress}%` }} />
																	</div>
																)}
																{round.notes && (
																	<p className="text-sm text-gray-600 border-t border-gray-100 pt-3">{round.notes}</p>
																)}
																<div>
																	<h4 className="text-sm font-semibold text-gray-800 mb-2">Network commitments</h4>
																	{!(round.commitments && round.commitments.length > 0) ? (
																		<p className="text-sm text-gray-500">No network commitments recorded for this tranche.</p>
																	) : (
																		<div className="space-y-4">
																			<div className="overflow-x-auto rounded-lg border border-gray-200">
																				<table className="min-w-full divide-y divide-gray-200 text-sm">
																					<thead className="bg-gray-50">
																						<tr>
																							<th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Network</th>
																							<th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Committed</th>
																							<th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Disbursed</th>
																							<th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
																							<th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
																						</tr>
																					</thead>
																					<tbody className="divide-y divide-gray-100 bg-white">
																						{round.commitments.map((c) => {
																							const dp = Math.min(Number(c.disbursementProgress) || 0, 100);
																							return (
																								<tr key={c.id} className="align-top">
																									<td className="px-4 py-3">
																										<div className="flex flex-wrap items-center gap-2">
																											<span className="font-medium text-gray-900">{c.networkName || '—'}</span>
																											<span className="inline-flex px-1.5 py-0.5 text-[10px] font-semibold rounded-md bg-teal-100 text-teal-800 border border-teal-200/60">Network</span>
																										</div>
																										{c.networkEmail && <div className="text-xs text-gray-500 mt-0.5">{c.networkEmail}</div>}
																									</td>
																									<td className="px-4 py-3 whitespace-nowrap">{formatMoney(c.committedAmount)}</td>
																									<td className="px-4 py-3">
																										<div className="whitespace-nowrap">{formatMoney(c.disbursedAmount)}</div>
																										<div className="mt-1 w-24 bg-gray-100 rounded-full h-1.5 overflow-hidden">
																											<div className="bg-teal-500 h-1.5 rounded-full" style={{ width: `${dp}%` }} />
																										</div>
																										<span className="text-[11px] text-gray-500">{dp.toFixed(0)}% disbursed</span>
																									</td>
																									<td className="px-4 py-3 text-gray-700">{c.investmentType || '—'}</td>
																									<td className="px-4 py-3">
																										<span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${roundStatusBadgeClass(c.status)}`}>
																											{c.status || '—'}
																										</span>
																									</td>
																								</tr>
																							);
																						})}
																					</tbody>
																				</table>
																			</div>
																			{round.commitments.some((c) => c.participations && c.participations.length > 0) && (
																				<div className="rounded-lg border border-gray-200 bg-white p-4">
																					<p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Individual participations (by network commitment)</p>
																					<div className="space-y-4">
																						{round.commitments.filter((c) => c.participations && c.participations.length > 0).map((c) => (
																							<div key={c.id}>
																								<p className="text-sm font-medium text-gray-800 mb-2">{c.networkName}</p>
																								<ul className="space-y-2 border-l-2 border-emerald-200 pl-3 ml-1">
																									{c.participations.map((p) => (
																										<li key={p.id} className="text-sm flex flex-wrap justify-between gap-2 items-start">
																											<span className="min-w-0">
																												<span className="font-medium text-gray-900">{p.investorName || '—'}</span>
																												{p.investorEmail && <span className="text-gray-500 text-xs block">{p.investorEmail}</span>}
																												<span className="inline-flex mt-1 px-1.5 py-0.5 text-[10px] font-semibold rounded bg-emerald-100 text-emerald-800">Individual</span>
																											</span>
																											<span className="text-gray-700 shrink-0">{formatMoney(p.amount)} {p.participationPercentage != null && <span className="text-gray-500">({Number(p.participationPercentage).toFixed(2)}%)</span>}</span>
																										</li>
																									))}
																								</ul>
																							</div>
																						))}
																					</div>
																				</div>
																			)}
																		</div>
																	)}
																</div>
															</div>
														</div>
													);
												})}
											</div>
										)}
									</div>
								</section>

								<section className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
									<div className="px-6 py-4 border-b border-gray-200">
										<h2 className="text-lg font-semibold text-gray-900">Cap table investments</h2>
										<p className="text-sm text-gray-500 mt-0.5">
											Direct records linked to your startup. <span className="text-gray-700 font-medium">Investor network</span> rows are the syndicate account;
											{' '}<span className="text-gray-700 font-medium">individual</span> rows are single investor accounts. Expand a row for full terms.
										</p>
									</div>
									<div className="overflow-x-auto">
										<table className="min-w-full divide-y divide-gray-200">
											<thead className="bg-gray-50">
												<tr>
													<th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase w-10" aria-hidden />
													<th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Investor</th>
													<th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Source</th>
													<th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
													<th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
													<th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Round / stage</th>
													<th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
													<th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Holding %</th>
													<th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Shares</th>
													<th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
												</tr>
											</thead>
											<tbody className="bg-white divide-y divide-gray-200">
												{(!investments || investments.length === 0) ? (
													<tr>
														<td colSpan="10" className="px-6 py-12 text-center text-gray-500">
															No cap table investments recorded yet.
														</td>
													</tr>
												) : (
													<>
														{capInvestmentGroups.network.length > 0 && (
															<>
																<tr className="bg-teal-50/90 border-y border-teal-100">
																	<td colSpan="10" className="px-4 py-2.5 text-xs font-semibold uppercase tracking-wide text-teal-900">
																		Investor network · {capInvestmentGroups.network.length}{' '}
																		{capInvestmentGroups.network.length === 1 ? 'record' : 'records'}
																	</td>
																</tr>
																<CapInvestmentRows
																	investments={capInvestmentGroups.network}
																	expandedInvestmentId={expandedInvestmentId}
																	setExpandedInvestmentId={setExpandedInvestmentId}
																	formatMoney={formatMoney}
																	formatDateTime={formatDateTime}
																	onEditInvestment={openEditInvestment}
																	canEditInvestment={canStartupEditInvestment}
																	sourceBadge={{ label: 'Network', className: 'bg-teal-100 text-teal-800 border-teal-200/60' }}
																/>
															</>
														)}
														{capInvestmentGroups.individual.length > 0 && (
															<>
																<tr className="bg-emerald-50/90 border-y border-emerald-100">
																	<td colSpan="10" className="px-4 py-2.5 text-xs font-semibold uppercase tracking-wide text-emerald-900">
																		Individual investors · {capInvestmentGroups.individual.length}{' '}
																		{capInvestmentGroups.individual.length === 1 ? 'record' : 'records'}
																	</td>
																</tr>
																<CapInvestmentRows
																	investments={capInvestmentGroups.individual}
																	expandedInvestmentId={expandedInvestmentId}
																	setExpandedInvestmentId={setExpandedInvestmentId}
																	formatMoney={formatMoney}
																	formatDateTime={formatDateTime}
																	onEditInvestment={openEditInvestment}
																	canEditInvestment={canStartupEditInvestment}
																	sourceBadge={{ label: 'Individual', className: 'bg-emerald-100 text-emerald-800 border-emerald-200/60' }}
																/>
															</>
														)}
													</>
												)}
											</tbody>
										</table>
									</div>
								</section>
							</div>
						)}

						{activeTab === 'applications' && (
							<div className="space-y-6">
								<div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
									<h3 className="text-lg font-semibold text-gray-900 mb-4">Apply for investment</h3>
									<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
										<div>
											<label className="block text-sm font-medium text-gray-700 mb-1">Apply to</label>
											<select
												value={applicationForm.targetType}
												onChange={(e) => setApplicationForm((prev) => ({ ...prev, targetType: e.target.value, targetUserId: '' }))}
												className="w-full px-3 py-2 border border-gray-300 rounded-lg"
											>
												<option value="INVESTOR_NETWORK">Investor Network</option>
												<option value="INVESTOR">Investor</option>
											</select>
										</div>
										<div>
											<label className="block text-sm font-medium text-gray-700 mb-1">Target</label>
											<select
												value={applicationForm.targetUserId}
												onChange={(e) => setApplicationForm((prev) => ({ ...prev, targetUserId: e.target.value }))}
												className="w-full px-3 py-2 border border-gray-300 rounded-lg"
											>
												<option value="">Select target</option>
												{(applicationTargets[applicationForm.targetType] || []).map((t) => (
													<option key={t.id} value={t.id}>{t.organizationName || t.name}</option>
												))}
											</select>
										</div>
										<div>
											<label className="block text-sm font-medium text-gray-700 mb-1">Pitch deck file</label>
											<select
												value={applicationForm.pitchDeckFileId}
												onChange={(e) => setApplicationForm((prev) => ({ ...prev, pitchDeckFileId: e.target.value }))}
												className="w-full px-3 py-2 border border-gray-300 rounded-lg"
											>
												<option value="">Select uploaded file (PPT/PPTX/PDF)</option>
												{files.map((f) => <option key={f.id} value={f.id}>{f.originalName}</option>)}
											</select>
											<div className="mt-2">
												<label className="inline-flex items-center px-3 py-1.5 bg-gray-100 text-gray-700 rounded-lg text-sm cursor-pointer hover:bg-gray-200">
													<input
														type="file"
														accept=".ppt,.pptx,.pdf,application/pdf,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation"
														className="hidden"
														onChange={async (e) => {
															const file = e.target.files?.[0];
															if (!file) return;
															try {
																setUploadingApplicationDeck(true);
																const formData = new FormData();
																formData.append('file', file);
																formData.append('moduleType', 'STARTUP');
																formData.append('moduleId', user?.id);
																formData.append('documentType', 'PITCH_DECK');
																const response = await fetch(getApiUrl('files/upload'), {
																	method: 'POST',
																	headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
																	body: formData
																});
																if (!response.ok) {
																	const err = await response.json().catch(() => ({}));
																	throw new Error(err.message || err.error || 'Failed to upload pitch deck');
																}
																const data = await response.json().catch(() => null);
																await loadStartupData();
																if (data?.file?.id) {
																	setApplicationForm((prev) => ({ ...prev, pitchDeckFileId: String(data.file.id) }));
																}
																setAlert({ isOpen: true, title: 'Success', message: 'Pitch deck uploaded successfully.', type: 'success' });
															} catch (error) {
																setAlert({ isOpen: true, title: 'Error', message: error.message || 'Failed to upload pitch deck', type: 'error' });
															} finally {
																setUploadingApplicationDeck(false);
																e.target.value = '';
															}
														}}
													/>
													{uploadingApplicationDeck ? 'Uploading...' : 'Upload pitch deck'}
												</label>
											</div>
										</div>
										<div className="md:col-span-2">
											<label className="block text-sm font-medium text-gray-700 mb-1">Message</label>
											<textarea
												value={applicationForm.message}
												onChange={(e) => setApplicationForm((prev) => ({ ...prev, message: e.target.value }))}
												className="w-full px-3 py-2 border border-gray-300 rounded-lg"
												rows={3}
											/>
										</div>
									</div>
									<div className="mt-4 flex justify-end">
										<button
											type="button"
											disabled={submittingApplication}
											onClick={async () => {
												if (!applicationForm.targetUserId || !applicationForm.pitchDeckFileId) {
													setAlert({ isOpen: true, title: 'Validation', message: 'Select target and pitch deck file.', type: 'error' });
													return;
												}
												try {
													setSubmittingApplication(true);
													await startupAPI.submitInvestmentApplication({
														targetType: applicationForm.targetType,
														targetUserId: Number(applicationForm.targetUserId),
														pitchDeckFileId: Number(applicationForm.pitchDeckFileId),
														message: applicationForm.message
													});
													setApplicationForm((prev) => ({ ...prev, targetUserId: '', pitchDeckFileId: '', message: '' }));
													await loadStartupData();
													setAlert({ isOpen: true, title: 'Success', message: 'Investment application submitted.', type: 'success' });
												} catch (error) {
													setAlert({ isOpen: true, title: 'Error', message: error.message || 'Failed to submit application', type: 'error' });
												} finally {
													setSubmittingApplication(false);
												}
											}}
											className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-60"
										>
											{submittingApplication ? 'Submitting...' : 'Submit Application'}
										</button>
									</div>
								</div>
								<div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
									<h3 className="text-lg font-semibold text-gray-900 mb-4">My applications</h3>
									{myApplications.length === 0 ? (
										<p className="text-sm text-gray-500">No applications submitted yet.</p>
									) : (
										<div className="overflow-x-auto">
											<table className="min-w-full divide-y divide-gray-200">
												<thead className="bg-gray-50">
													<tr>
														<th className="px-3 py-2 text-left text-xs text-gray-500 uppercase">Target</th>
														<th className="px-3 py-2 text-left text-xs text-gray-500 uppercase">Type</th>
														<th className="px-3 py-2 text-left text-xs text-gray-500 uppercase">Pitch deck</th>
														<th className="px-3 py-2 text-left text-xs text-gray-500 uppercase">Status</th>
													</tr>
												</thead>
												<tbody className="divide-y divide-gray-100">
													{myApplications.map((app) => (
														<tr key={app.id}>
															<td className="px-3 py-2 text-sm text-gray-900">{app.targetName}</td>
															<td className="px-3 py-2 text-sm text-gray-700">{app.targetType}</td>
															<td className="px-3 py-2 text-sm text-gray-700">{app.pitchDeckFileName}</td>
															<td className="px-3 py-2 text-sm font-medium">{app.status}</td>
														</tr>
													))}
												</tbody>
											</table>
										</div>
									)}
								</div>
							</div>
						)}

						{/* Stage History Tab */}
						{activeTab === 'stage-history' && (
							<div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
								<div className="px-6 py-4 border-b border-gray-200">
									<p className="text-sm text-gray-500">Funding stage progression</p>
								</div>
								<div className="p-6">
									{(!stageHistory || stageHistory.length === 0) ? (
										<div className="text-center py-12 text-gray-500">
											<p>No stage history yet. Your current stage will appear here once set in Edit Profile.</p>
										</div>
									) : (
										<>
											<div className="flex justify-between items-center mb-6">
												{stageHistory.map((s, index) => (
													<div key={index} className="flex flex-col items-center flex-1">
														<div className={`w-12 h-12 rounded-full flex items-center justify-center ${index === stageHistory.length - 1 ? 'bg-emerald-600 text-white' : 'bg-gray-200 text-gray-600'}`}>
															<svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
																<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
															</svg>
														</div>
														<p className="mt-2 text-sm font-medium text-gray-900">{s.stage}</p>
														<p className="text-xs text-gray-500">{s.date}</p>
													</div>
												))}
											</div>
											<table className="min-w-full divide-y divide-gray-200">
												<thead className="bg-gray-50">
													<tr>
														<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Stage</th>
														<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
														<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
													</tr>
												</thead>
												<tbody className="bg-white divide-y divide-gray-200">
													{stageHistory.map((s, index) => (
														<tr key={index}>
															<td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{s.stage}</td>
															<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{s.date}</td>
															<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{s.amount || '—'}</td>
														</tr>
													))}
												</tbody>
											</table>
										</>
									)}
								</div>
							</div>
						)}

						{/* Directors Tab */}
						{activeTab === 'directors' && (
							<div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
								<div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between gap-3">
									<div>
										<p className="text-sm text-gray-500">Manage directors from your startup profile.</p>
									</div>
									<button
										onClick={() => { setEditModalDirectorsOnly(true); setShowEditModal(true); }}
										className="px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-emerald-600 to-teal-600 rounded-lg hover:from-emerald-700 hover:to-teal-700 transition"
									>
										Add Directors
									</button>
								</div>
								<div className="overflow-x-auto">
									{!(Array.isArray(directors) && directors.length > 0) ? (
										<div className="px-6 py-12 text-center text-gray-500">
											<p>No directors added yet.</p>
											<button
												onClick={() => { setEditModalDirectorsOnly(true); setShowEditModal(true); }}
												className="mt-4 px-4 py-2 text-sm font-medium text-emerald-700 bg-emerald-50 rounded-lg hover:bg-emerald-100 transition"
											>
												Add Directors Now
											</button>
										</div>
									) : (
										<table className="min-w-full divide-y divide-gray-200">
											<thead className="bg-gray-50">
												<tr>
													<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
													<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
													<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Role</th>
													<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Shareholding</th>
												</tr>
											</thead>
											<tbody className="bg-white divide-y divide-gray-200">
												{(directors || []).map((d, idx) => (
													<tr key={d?.id ?? `dir-${idx}`} className="hover:bg-gray-50">
														<td className="px-6 py-4 whitespace-nowrap">
															<div className="font-medium text-gray-900">{d.name}</div>
															{d.email && <div className="text-xs text-gray-500">{d.email}</div>}
														</td>
														<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{d.category ? d.category.replace(/_/g, ' ') : '—'}</td>
														<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{d.role || d.designation || '—'}</td>
														<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{d.shareholding || '—'}</td>
													</tr>
												))}
											</tbody>
										</table>
									)}
								</div>
							</div>
						)}

						{/* Files Tab */}
						{activeTab === 'files' && (
							<div className="space-y-6">
								{/* Action Bar */}
								<div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-6 p-6">
									<div className="flex flex-col sm:flex-row justify-between items-center space-y-4 sm:space-y-0">
										<div className="flex items-center space-x-4 w-full sm:w-auto">
											<div className="relative flex-1 sm:flex-initial">
												<input
													type="text"
													placeholder="Search files..."
													value={searchTerm}
													onChange={(e) => setSearchTerm(e.target.value)}
													className="w-full sm:w-64 pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
												/>
												<svg className="absolute left-3 top-2.5 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
													<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
												</svg>
											</div>
										</div>
										<button
											onClick={() => setShowUploadModal(true)}
											className="w-full sm:w-auto px-6 py-2 bg-gradient-to-r from-emerald-600 to-teal-600 text-white font-medium rounded-lg hover:from-emerald-700 hover:to-teal-700 transition duration-200 flex items-center justify-center space-x-2"
										>
											<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
												<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
											</svg>
											<span>Upload New File</span>
										</button>
									</div>
								</div>

								{/* Files Grid */}
								<div className="bg-white rounded-xl shadow-sm border border-gray-200">
									<div className="p-6">
										{filteredFiles.length === 0 ? (
											<div className="text-center py-16">
												<svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
													<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
												</svg>
												<p className="text-lg font-medium text-gray-900 mb-2">
													{searchTerm ? 'No files found' : 'No documents yet'}
												</p>
												<p className="text-sm text-gray-500 mb-6">
													{searchTerm ? 'Try adjusting your search terms' : 'Upload your first document to get started'}
												</p>
												{!searchTerm && (
													<button
														onClick={() => setShowUploadModal(true)}
														className="px-6 py-2 bg-gradient-to-r from-emerald-600 to-teal-600 text-white font-medium rounded-lg hover:from-emerald-700 hover:to-teal-700 transition duration-200"
													>
														Upload First Document
													</button>
												)}
											</div>
										) : (
											<>
											<div className="overflow-x-auto overflow-y-auto max-h-[65vh]">
												<table className="min-w-full divide-y divide-gray-200">
													<thead className="bg-gray-50">
														<tr>
															<th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
																Name
															</th>
															<th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
																Category
															</th>
															<th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
																Size
															</th>
															<th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
																Date
															</th>
															<th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
																Actions
															</th>
														</tr>
													</thead>
													<tbody className="bg-white divide-y divide-gray-200">
														{paginatedFiles.map((file) => (
															<tr key={file.id} className="hover:bg-gray-50 transition-colors">
																<td className="px-6 py-4 whitespace-nowrap">
																	<div className="flex items-center">
																		<div className="flex-shrink-0 h-10 w-10 bg-blue-100 rounded-lg flex items-center justify-center text-blue-600">
																			<svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
																				<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
																			</svg>
																		</div>
																		<div className="ml-4">
																			<div className="text-sm font-medium text-gray-900 group relative">
																				{file.originalName}
																				{/* Description tooltip */}
																				{file.description && (
																					<span className="absolute left-0 -bottom-1 transform translate-y-full w-64 p-2 bg-gray-800 text-white text-xs rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity z-50 pointer-events-none">
																						{file.description}
																					</span>
																				)}
																			</div>
																			{file.description && (
																				<div className="text-xs text-gray-500 truncate max-w-xs">{file.description}</div>
																			)}
																		</div>
																	</div>
																</td>
																<td className="px-6 py-4 whitespace-nowrap">
																	{file.fileCategory ? (
																		<span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${file.fileCategory.includes('PITCH') || file.fileCategory.includes('PRESENTATION') || file.fileCategory.includes('DECK') ? 'bg-purple-100 text-purple-800' :
																			file.fileCategory.includes('FINANCIAL') || file.fileCategory.includes('VALUATION') || file.fileCategory.includes('CAP_TABLE') || file.fileCategory.includes('BALANCE') || file.fileCategory.includes('CASH_FLOW') || file.fileCategory.includes('P_L') ? 'bg-green-100 text-green-800' :
																				file.fileCategory.includes('LEGAL') || file.fileCategory.includes('AGREEMENT') || file.fileCategory.includes('SHA') || file.fileCategory.includes('TERM') || file.fileCategory.includes('NDA') || file.fileCategory.includes('PATENT') || file.fileCategory.includes('TRADEMARK') || file.fileCategory.includes('IP') ? 'bg-blue-100 text-blue-800' :
																					file.fileCategory.includes('BUSINESS') || file.fileCategory.includes('MARKET') || file.fileCategory.includes('COMPETITIVE') || file.fileCategory.includes('PRODUCT') || file.fileCategory.includes('TECHNICAL') ? 'bg-orange-100 text-orange-800' :
																						file.fileCategory.includes('COMPLIANCE') || file.fileCategory.includes('LICENSE') || file.fileCategory.includes('REGULATORY') || file.fileCategory.includes('TAX') ? 'bg-yellow-100 text-yellow-800' :
																							file.fileCategory.includes('OPERATIONAL') || file.fileCategory.includes('HR') || file.fileCategory.includes('POLICY') || file.fileCategory.includes('PROCESS') ? 'bg-indigo-100 text-indigo-800' :
																								file.fileCategory.includes('MARKETING') || file.fileCategory.includes('SALES') || file.fileCategory.includes('BRAND') ? 'bg-pink-100 text-pink-800' :
																									'bg-gray-100 text-gray-800'
																			}`}>
																			{file.fileCategory.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
																		</span>
																	) : (
																		<span className="text-gray-400">-</span>
																	)}
																</td>
																<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
																	{file.fileSize ? `${(file.fileSize / 1024 / 1024).toFixed(2)} MB` : '-'}
																</td>
																<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
																	{formatDateTime(file.uploadedAt)}
																</td>
																<td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
																	<div className="flex justify-end space-x-3">
																		<button
																			onClick={() => previewFile(file)}
																			className="text-blue-600 hover:text-blue-900 transition-colors"
																			title="Preview"
																		>
																			<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
																				<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
																				<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
																			</svg>
																		</button>
																		<button
																			onClick={() => downloadFile(file)}
																			className="text-green-600 hover:text-green-900 transition-colors"
																			title="Download"
																		>
																			<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
																				<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
																			</svg>
																		</button>
																		<button
																			onClick={() => handleShare(file)}
																			className="text-indigo-600 hover:text-indigo-900 transition-colors"
																			title="Share / Visibility"
																		>
																			<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
																				<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
																			</svg>
																		</button>
																		<button
																			onClick={() => deleteFile(file.id)}
																			className="text-red-600 hover:text-red-900 transition-colors"
																			title="Delete"
																		>
																			<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
																				<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
																			</svg>
																		</button>
																		{/* Trace Views Button */}
																		{(file.uploadedById === user?.id) && (
																			<button
																				onClick={() => {
																					setTraceFile({ id: file.id, name: file.originalName });
																					setShowTraceModal(true);
																				}}
																				className="text-orange-600 hover:text-orange-900 transition-colors"
																				title="Trace Views"
																			>
																				<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
																					<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
																				</svg>
																			</button>
																		)}
																	</div>
																</td>
															</tr>
														))}
													</tbody>
												</table>
											</div>
											<div className="mt-4 flex flex-col sm:flex-row items-center justify-between gap-3">
												<p className="text-sm text-gray-600">
													Showing <span className="font-medium">{filteredFiles.length === 0 ? 0 : ((filesPage - 1) * FILES_PER_PAGE) + 1}</span>
													{' '}to <span className="font-medium">{Math.min(filesPage * FILES_PER_PAGE, filteredFiles.length)}</span>
													{' '}of <span className="font-medium">{filteredFiles.length}</span> files
												</p>
												<div className="flex items-center gap-2">
													<button
														onClick={() => setFilesPage((p) => Math.max(1, p - 1))}
														disabled={filesPage === 1}
														className="px-3 py-1.5 text-sm rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
													>
														Previous
													</button>
													<span className="text-sm text-gray-600 px-2">
														Page {filesPage} of {totalFilePages}
													</span>
													<button
														onClick={() => setFilesPage((p) => Math.min(totalFilePages, p + 1))}
														disabled={filesPage === totalFilePages}
														className="px-3 py-1.5 text-sm rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
													>
														Next
													</button>
												</div>
											</div>
											</>
										)}
									</div>
								</div>
							</div>
						)}
						{/* Audit Log Tab */}
						{activeTab === 'audit' && (
							<AuditLogTable />
						)}
					</>
				)}
			</div>
			</main>

			{/* Edit Profile = CreateStartupModal in edit mode (same fields as Create Startup) */}
			<CreateStartupModal
				isOpen={showEditModal}
				onClose={() => { setShowEditModal(false); setEditModalDirectorsOnly(false); }}
				onSuccess={() => {
					loadStartupData();
					setShowEditModal(false);
					setEditModalDirectorsOnly(false);
					setAlert({
						isOpen: true,
						title: 'Success',
						message: editModalDirectorsOnly ? 'Directors saved successfully!' : 'Profile updated successfully!',
						type: 'success'
					});
				}}
				initialData={startupProfile}
				isEdit={true}
				isSelfEdit={true}
				directorsOnly={editModalDirectorsOnly}
			/>

			<ChangePasswordModal
				isOpen={showPasswordModal}
				onClose={() => setShowPasswordModal(false)}
				onSuccess={() =>
					setAlert({ isOpen: true, title: 'Success', message: 'Password changed successfully!', type: 'success' })
				}
			/>

			{/* Upload Modal */}
			{showUploadModal && (
				<div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
					<div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
						<div className="p-6 border-b border-gray-200">
							<div className="flex justify-between items-center">
								<h3 className="text-xl font-semibold text-gray-900">Upload Document</h3>
								<button
									onClick={() => {
										setShowUploadModal(false);
										setSelectedFile(null);
									}}
									className="text-gray-400 hover:text-gray-600 transition-colors"
								>
									<svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
										<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
									</svg>
								</button>
							</div>
						</div>

						<div className="p-6">
							<FileUpload
								moduleType="STARTUP"
								moduleId={user?.id}
								onUploadSuccess={() => {
									loadStartupData();
									setShowUploadModal(false);
									setAlert({ isOpen: true, title: 'Success', message: 'File uploaded successfully', type: 'success' });
								}}
								onUploadError={(msg) => setAlert({ isOpen: true, title: 'Error', message: msg, type: 'error' })}
								defaultVisibility="PRIVATE"
							/>
						</div>
					</div>
				</div>
			)}

			{showEditInvestmentModal && editingInvestmentId && (
				<div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
					<div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
						<div className="sticky top-0 bg-white px-6 py-4 border-b border-gray-200">
							<div className="flex items-center justify-between">
								<h3 className="text-lg font-semibold text-gray-900">Edit Investment</h3>
								<button onClick={closeEditInvestmentModal} className="text-gray-400 hover:text-gray-600">
									<svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
										<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
									</svg>
								</button>
							</div>
							<p className="text-sm text-gray-500 mt-1">
								Startup can edit only records not created by investor network.
							</p>
						</div>
						<div className="p-6">
							<InvestmentTabbedForm value={editingInvestmentForm} onChange={setEditingInvestmentForm} showGovernance={false} />
						</div>
						<div className="sticky bottom-0 bg-gray-50 px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
							<button
								onClick={closeEditInvestmentModal}
								className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
								disabled={savingInvestment}
							>
								Cancel
							</button>
							<button
								onClick={handleUpdateInvestment}
								className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300"
								disabled={savingInvestment}
							>
								{savingInvestment ? 'Saving...' : 'Save Changes'}
							</button>
						</div>
					</div>
				</div>
			)}

			{/* Alert Dialog */}
			<AlertDialog
				isOpen={alert.isOpen}
				onClose={() => setAlert({ ...alert, isOpen: false })}
				title={alert.title}
				message={alert.message}
				type={alert.type}
			/>

			{/* Confirm Dialog */}
			<ConfirmDialog
				isOpen={confirm.isOpen}
				onClose={() => setConfirm({ ...confirm, isOpen: false })}
				onConfirm={confirm.onConfirm || (() => { })}
				title={confirm.title}
				message={confirm.message}
				type={confirm.type}
			/>
			{/* File Share Modal */}
			{showShareModal && sharingFile && (
				<FileShareModal
					file={sharingFile}
					isOpen={showShareModal}
					onClose={() => {
						setShowShareModal(false);
						setSharingFile(null);
					}}
					onSuccess={() => {
						loadStartupData();
						setAlert({ isOpen: true, title: 'Success', message: 'File permissions updated successfully', type: 'success' });
					}}
				/>
			)}

			<FileTraceModal
				isOpen={showTraceModal}
				onClose={() => {
					setShowTraceModal(false);
					setTraceFile(null);
				}}
				fileId={traceFile?.id}
				fileName={traceFile?.name}
			/>
		</div>
	);
};

function CapInvestmentRows({
	investments,
	expandedInvestmentId,
	setExpandedInvestmentId,
	formatMoney,
	formatDateTime,
	sourceBadge,
	onEditInvestment,
	canEditInvestment
}) {
	return investments.map((inv) => (
		<React.Fragment key={inv.id}>
			<tr className="hover:bg-gray-50">
				<td className="px-4 py-4">
					<button
						type="button"
						onClick={() => setExpandedInvestmentId(expandedInvestmentId === inv.id ? null : inv.id)}
						className="p-1 rounded-md text-gray-500 hover:bg-gray-100"
						aria-expanded={expandedInvestmentId === inv.id}
						aria-label={expandedInvestmentId === inv.id ? 'Collapse details' : 'Expand details'}
					>
						<svg className={`w-5 h-5 transition-transform ${expandedInvestmentId === inv.id ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
							<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
						</svg>
					</button>
				</td>
				<td className="px-4 py-4 whitespace-nowrap">
					<div className="font-medium text-gray-900">{inv.investorName || '—'}</div>
					<div className="text-xs text-gray-500">{inv.investorEmail}</div>
				</td>
				<td className="px-4 py-4 whitespace-nowrap">
					<span className={`inline-flex px-2 py-0.5 text-[11px] font-semibold rounded-md border ${sourceBadge.className}`}>
						{sourceBadge.label}
					</span>
				</td>
				<td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
					{formatMoney(inv.investmentAmount, inv.currency)}
				</td>
				<td className="px-4 py-4 whitespace-nowrap text-sm text-gray-700">
					{inv.investmentDate ? formatDateTime(inv.investmentDate) : '—'}
				</td>
				<td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">{inv.investmentStage || '—'}</td>
				<td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">{inv.investmentType || '—'}</td>
				<td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
					{inv.equityPercentage != null ? `${Number(inv.equityPercentage).toFixed(4).replace(/\.?0+$/, '')}%` : '—'}
				</td>
				<td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">{inv.numberOfShares ?? '—'}</td>
				<td className="px-4 py-4 whitespace-nowrap">
					<span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${inv.status === 'ACTIVE' ? 'bg-emerald-100 text-emerald-800' : inv.status === 'EXITED' ? 'bg-amber-100 text-amber-900' : 'bg-gray-100 text-gray-800'}`}>
						{inv.status || '—'}
					</span>
				</td>
			</tr>
			{expandedInvestmentId === inv.id && (
				<tr>
					<td colSpan="10" className="px-4 py-4 bg-slate-50 border-t border-gray-100">
						<div className="space-y-4">
							<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-3 text-sm">
								<div>
									<span className="text-gray-500">Recorded</span>
									<p className="font-medium text-gray-900">
										{inv.createdAt ? formatDateTime(inv.createdAt) : '—'}
										{inv.updatedAt && inv.updatedAt !== inv.createdAt && (
											<span className="text-xs text-gray-500 block">Updated {formatDateTime(inv.updatedAt)}</span>
										)}
									</p>
								</div>
							</div>
							<div className="flex justify-end">
								{canEditInvestment?.(inv) ? (
									<button
										type="button"
										onClick={() => onEditInvestment?.(inv)}
										className="px-3 py-1.5 text-xs font-semibold text-blue-700 bg-blue-100 rounded-md hover:bg-blue-200"
									>
										Edit Investment
									</button>
								) : (
									<span className="text-xs text-amber-700 bg-amber-100 rounded-md px-2.5 py-1">
										Added by investor network — startup edit disabled
									</span>
								)}
							</div>
							<InvestmentTabbedDetails investment={inv} formatMoney={formatMoney} formatDateTime={formatDateTime} />
						</div>
					</td>
				</tr>
			)}
		</React.Fragment>
	));
}

export default StartupDashboard;
